import React from "react";

interface ModeRendererProps {
  brand: string;
  today: string;
  Copy: string;
}

export function VasarelyCapgeminiMode({ brand }: ModeRendererProps) {
  const width = 794;
  const height = 1123;

  // Capgemini gradient - corporate blue digital clarity
  const bgGradient = (
    <defs>
      <linearGradient id="capgeminiGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" style={{ stopColor: "#0070AD", stopOpacity: 1 }} />
        <stop offset="50%" style={{ stopColor: "#0099CC", stopOpacity: 1 }} />
        <stop offset="100%" style={{ stopColor: "#00BFFF", stopOpacity: 1 }} />
      </linearGradient>
    </defs>
  );

  // Blue polygons - modular transformation blocks
  const bluePolygons: JSX.Element[] = [];
  const polyGrid = 14;
  const polySize = 50;
  
  for (let i = 0; i < polyGrid; i++) {
    for (let j = 0; j < polyGrid; j++) {
      const x = -350 + i * polySize;
      const y = -350 + j * polySize;
      const variant = (i + j) % 3;
      
      let points = "";
      if (variant === 0) {
        // Hexagon
        const hexPoints: string[] = [];
        for (let h = 0; h < 6; h++) {
          const angle = (h / 6) * 2 * Math.PI;
          const px = x + Math.cos(angle) * polySize / 2;
          const py = y + Math.sin(angle) * polySize / 2;
          hexPoints.push(`${px},${py}`);
        }
        points = hexPoints.join(" ");
      } else if (variant === 1) {
        // Square
        points = `${x - polySize / 2},${y - polySize / 2} ${x + polySize / 2},${y - polySize / 2} ${x + polySize / 2},${y + polySize / 2} ${x - polySize / 2},${y + polySize / 2}`;
      } else {
        // Triangle
        points = `${x},${y - polySize / 2} ${x + polySize / 2},${y + polySize / 2} ${x - polySize / 2},${y + polySize / 2}`;
      }
      
      bluePolygons.push(
        <polygon
          key={`poly-${i}-${j}`}
          points={points}
          fill="none"
          stroke="#FFFFFF"
          strokeWidth={1.5}
          opacity={0.15}
        />
      );
    }
  }

  // Data streams - flowing transformation pathways
  const dataStreams: JSX.Element[] = [];
  for (let stream = 0; stream < 12; stream++) {
    const points: string[] = [];
    const startX = -340 + stream * 56;
    
    for (let p = 0; p <= 40; p++) {
      const t = p / 40;
      const x = startX + Math.sin(t * Math.PI * 3 + stream * 0.6) * 30;
      const y = -500 + t * 1000;
      points.push(`${x},${y}`);
    }
    
    dataStreams.push(
      <polyline
        key={`stream-${stream}`}
        points={points.join(" ")}
        fill="none"
        stroke="#0099CC"
        strokeWidth={2}
        opacity={0.3}
      />
    );
  }

  // Modular matrices - system architecture blocks
  const modularMatrices: JSX.Element[] = [];
  const matrixLayers = 8;
  
  for (let layer = 0; layer < matrixLayers; layer++) {
    const size = 100 + layer * 45;
    const offset = layer * 15;
    
    modularMatrices.push(
      <g key={`matrix-${layer}`}>
        <rect
          x={-size / 2 + offset}
          y={-size / 2 + offset}
          width={size}
          height={size}
          fill="none"
          stroke="#FFFFFF"
          strokeWidth={2}
          opacity={0.3 - layer * 0.025}
          rx={8}
        />
        <rect
          x={-size / 2 + offset + 10}
          y={-size / 2 + offset + 10}
          width={size - 20}
          height={size - 20}
          fill="none"
          stroke="#00BFFF"
          strokeWidth={1.5}
          opacity={0.25 - layer * 0.02}
          rx={6}
        />
      </g>
    );
  }

  // Transformation framework nodes - consulting waypoints
  const frameworkNodes: JSX.Element[] = [];
  const nodePositions: [number, number][] = [
    [-250, -300],
    [0, -350],
    [250, -300],
    [-300, -100],
    [300, -100],
    [-250, 100],
    [0, 150],
    [250, 100],
    [-150, 320],
    [150, 320],
  ];
  
  nodePositions.forEach(([nx, ny], i) => {
    frameworkNodes.push(
      <g key={`framework-${i}`}>
        <circle
          cx={nx}
          cy={ny}
          r={35}
          fill="none"
          stroke="#FFFFFF"
          strokeWidth={2.5}
          opacity={0.5}
        />
        <circle
          cx={nx}
          cy={ny}
          r={24}
          fill="none"
          stroke="#0099CC"
          strokeWidth={2}
          opacity={0.4}
        />
        <circle
          cx={nx}
          cy={ny}
          r={12}
          fill="#00BFFF"
          opacity={0.6}
        />
      </g>
    );
  });

  // Governance map connections
  const governanceConnections: JSX.Element[] = [];
  const connections: [number, number][] = [
    [0, 1],
    [1, 2],
    [0, 3],
    [2, 4],
    [3, 5],
    [4, 7],
    [5, 6],
    [6, 7],
    [5, 8],
    [7, 9],
  ];
  
  connections.forEach(([a, b], i) => {
    const [x1, y1] = nodePositions[a];
    const [x2, y2] = nodePositions[b];
    
    governanceConnections.push(
      <line
        key={`connect-${i}`}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="#FFFFFF"
        strokeWidth={2}
        opacity={0.25}
        strokeDasharray="8,4"
      />
    );
  });

  // Digital clarity rings - alignment circles
  const clarityRings: JSX.Element[] = [];
  for (let ring = 1; ring <= 16; ring++) {
    const radius = ring * 25;
    clarityRings.push(
      <circle
        key={`clarity-${ring}`}
        cx={0}
        cy={0}
        r={radius}
        fill="none"
        stroke="#FFFFFF"
        strokeWidth={ring % 4 === 0 ? 2.5 : 1.5}
        opacity={0.3 - ring * 0.014}
      />
    );
  }

  // Consulting layers - strategic overlays
  const consultingLayers: JSX.Element[] = [];
  for (let layer = 0; layer < 6; layer++) {
    const width = 650 - layer * 80;
    const height = 450 - layer * 60;
    
    consultingLayers.push(
      <rect
        key={`consulting-${layer}`}
        x={-width / 2}
        y={-height / 2}
        width={width}
        height={height}
        fill="none"
        stroke="#0099CC"
        strokeWidth={2}
        opacity={0.2 - layer * 0.025}
        rx={12}
      />
    );
  }

  // Central Capgemini compass
  const capgeminiCompass = (
    <g opacity={0.5}>
      <circle cx={0} cy={0} r={50} fill="none" stroke="#FFFFFF" strokeWidth={3} />
      <circle cx={0} cy={0} r={35} fill="none" stroke="#00BFFF" strokeWidth={2.5} />
      <line x1={-45} y1={0} x2={45} y2={0} stroke="#0099CC" strokeWidth={2.5} />
      <line x1={0} y1={-45} x2={0} y2={45} stroke="#0099CC" strokeWidth={2.5} />
      <circle cx={0} cy={0} r={18} fill="#0099CC" opacity={0.4} />
    </g>
  );

  return (
    <svg width={width} height={height} xmlns="http://www.w3.org/2000/svg">
      {bgGradient}
      <rect width={width} height={height} fill="url(#capgeminiGradient)" />
      <g transform={`translate(${width / 2}, ${height / 2})`}>
        {consultingLayers}
        {bluePolygons}
        {dataStreams}
        {clarityRings}
        {modularMatrices}
        {governanceConnections}
        {frameworkNodes}
        {capgeminiCompass}
      </g>
    </svg>
  );
};
